/**
 * CartPage - Shopping cart
 * 
 * Features:
 * - View all cart items grouped by shop
 * - Update quantities, remove items
 * - Proceed to checkout
 * - Booking happens at checkout page
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import VariantLabel from '../../components/VariantLabel';
import { getCartItemImage, isPackage } from '../../utils/packageIcons';
import './CartPage.css';

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, itemCount, totalPrice, updateQuantity, removeItem, clearCart, loading } = useCart();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  // Group items by shop
  const itemsByShop = cartItems.reduce((acc, item) => {
    const shopId = item.shopId?._id || item.shopId;
    // For freelancers, show business name; for shops, show shop location name
    const shopName = item.shopId?.businessId?.name || item.shopId?.name || 'Unknown Shop';
    
    if (!acc[shopId]) {
      acc[shopId] = {
        shopId,
        shopName,
        items: []
      };
    }
    
    acc[shopId].items.push(item);
    return acc;
  }, {});

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch featured products for empty cart
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      if (itemCount === 0) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/featured?limit=6`
          );
          if (response.ok) {
            const data = await response.json();
            setFeaturedProducts(data.products || []);
          }
        } catch (error) {
        }
      }
    };
    fetchFeaturedProducts();
  }, [itemCount]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    await updateQuantity(itemId, newQuantity);
  };

  const handleRemoveItem = async (itemId) => {
    if (window.confirm('Remove this item from your cart?')) {
      await removeItem(itemId);
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };



  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allItemIds = cartItems.map(item => item._id);
      setSelectedItems(new Set(allItemIds));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      // Update selectAll state
      setSelectAll(newSet.size === cartItems.length);
      return newSet;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    if (window.confirm(`Remove ${selectedItems.size} selected ${selectedItems.size === 1 ? 'item' : 'items'} from your cart?`)) {
      for (const itemId of selectedItems) {
        await removeItem(itemId);
      }
      setSelectedItems(new Set());
      setSelectAll(false);
    }
  };

  const handleEmptyCart = async () => {
    if (window.confirm('Remove all items from your cart?')) {
      await clearCart();
      setSelectedItems(new Set());
      setSelectAll(false);
    }
  };

  const getShopSubtotal = (items) => {
    return items.reduce((sum, item) => sum + (item.priceAtAdd * item.quantity), 0);
  };

  const getItemName = (item) => {
    if (item.itemType === 'package') {
      return item.packageId?.name || 'Package';
    }
    return item.productId?.masterProductId?.name || 'Product';
  };

  const getItemPrice = (item) => {
    return item.priceAtAdd;
  };

  if (loading && cartItems.length === 0) {
    return (
      <>
        <CustomerHeader />
        <div className="cart-page-loading">
          <div className="loading-spinner"></div>
          <p>Loading your cart...</p>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (itemCount === 0) {
    return (
      <>
        <CustomerHeader />
        <div className="cart-page-empty">
          <div className="empty-cart-content">
            <div className="empty-cart-icon">🛒</div>
            <h2>Your cart is empty</h2>
            <p>Discover amazing beauty services and products!</p>
            
            <div className="empty-cart-actions">
              <button 
                className="primary-action-btn"
                onClick={() => navigate('/')}
              >
                🏪 Find Businesses
              </button>
              <button 
                className="secondary-action-btn"
                onClick={() => navigate('/products')}
              >
                🛍️ Browse Products
              </button>
            </div>

            {/* Featured Products */}
            {featuredProducts.length > 0 && (
              <div className="empty-cart-featured">
                <h3>✨ Popular Products</h3>
                <div className="featured-products-grid">
                  {featuredProducts.map(product => (
                    <div 
                      key={product._id} 
                      className="featured-product-card"
                      onClick={() => navigate(`/product/${product._id}`)}
                    >
                      <img 
                        src={product.images?.[0] || '/placeholder.png'} 
                        alt={product.name}
                      />
                      <div className="featured-product-info">
                        <h4>{product.name}</h4>
                        <p className="featured-product-price">${product.price}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Links */}
            <div className="empty-cart-links">
              <p>Looking for something specific?</p>
              <div className="quick-links">
                <button onClick={() => navigate('/products')}>Browse Products</button>
                <button onClick={() => navigate('/services')}>Book a Service</button>
                <button onClick={() => navigate('/')}>Find Businesses Near You</button>
              </div>
            </div>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div className="cart-page">
        <div className="cart-page-container">
          <div className="cart-header-section">
            <div>
              <h1>Shopping Cart</h1>
              <p className="cart-subtitle">
                {itemCount} {itemCount === 1 ? 'item' : 'items'} in your cart
              </p>
            </div>
            <div className="cart-bulk-actions">
              <label className="select-all-checkbox">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <span>Select All</span>
              </label>
              <button
                className="delete-selected-btn"
                onClick={handleDeleteSelected}
                disabled={selectedItems.size === 0 || loading}
              >
                Delete Selected ({selectedItems.size})
              </button>
              <button
                className="empty-cart-btn"
                onClick={handleEmptyCart}
                disabled={loading}
              >
                Empty Cart
              </button>
            </div>
          </div>

          {Object.entries(itemsByShop).map(([shopId, { shopName, items }]) => {
            // For freelancers, show brand name instead of address
            const firstItem = items[0];
            const isFreelancer = firstItem?.shopId?.isFreelancer || firstItem?.shopId?.businessType === 'freelancer';
            const displayName = isFreelancer 
              ? (firstItem?.shopId?.businessName || firstItem?.shopId?.brandName || shopName)
              : shopName;
            
            return (
            <div key={shopId} className="cart-shop-section">
              <h2 className="cart-shop-name">{displayName}</h2>
              
              {/* Cart Items */}
              <div className="cart-items-list">
                {items.map(item => (
                  <div key={item._id} className="cart-item">
                    <div className="cart-item-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item._id)}
                        onChange={() => handleSelectItem(item._id)}
                      />
                    </div>
                    <div className="cart-item-image">
                      <img src={getCartItemImage(item)} alt={getItemName(item)} />
                      {isPackage(item) && (
                        <div className="item-type-badge package">Package</div>
                      )}
                      {item.packageOptions?.isGift && (
                        <div className="gift-badge">🎁</div>
                      )}
                    </div>
                    
                    <div className="cart-item-details">
                      <h3 className="cart-item-name">{getItemName(item)}</h3>
                      {/* Phase 4: Display variant label */}
                      <VariantLabel item={item} showIcon={false} />
                      {item.productId?.description && (
                        <p className="cart-item-description">
                          {item.productId.description.substring(0, 100)}
                          {item.productId.description.length > 100 && '...'}
                        </p>
                      )}
                      {item.packageId?.totalSessions && (
                        <p className="cart-item-meta">
                          {item.packageId.totalSessions} sessions • 
                          {item.packageId.validityPeriod?.value ? ` Valid for ${item.packageId.validityPeriod.value} ${item.packageId.validityPeriod.unit || 'days'}` : ' No expiry'}
                        </p>
                      )}
                      {item.packageOptions?.recipientEmail && (
                        <p className="cart-item-gift-info">
                          🎁 Gift for: {item.packageOptions.recipientEmail}
                        </p>
                      )}
                    </div>

                    <div className="cart-item-quantity">
                      {item.itemType === 'product' ? (
                        <>
                          <button 
                            onClick={() => handleQuantityChange(item._id, item.quantity - 1)}
                            disabled={loading || item.quantity <= 1}
                          >
                            −
                          </button>
                          <span>{item.quantity}</span>
                          <button 
                            onClick={() => handleQuantityChange(item._id, item.quantity + 1)}
                            disabled={loading}
                          >
                            +
                          </button>
                        </>
                      ) : (
                        <span className="package-qty">Qty: {item.quantity}</span>
                      )}
                    </div>

                    <div className="cart-item-price">
                      ${getItemPrice(item).toFixed(2)}
                      {item.quantity > 1 && (
                        <span className="item-subtotal">
                          ${(getItemPrice(item) * item.quantity).toFixed(2)} total
                        </span>
                      )}
                    </div>

                    <button 
                      className="cart-item-remove"
                      onClick={() => handleRemoveItem(item._id)}
                      disabled={loading}
                      aria-label="Remove item"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              {/* Shop Subtotal */}
              <div className="cart-shop-subtotal">
                <span>Subtotal for {shopName}:</span>
                <span className="subtotal-amount">${getShopSubtotal(items).toFixed(2)}</span>
              </div>
            </div>
            );
          })}

          {/* Cart Summary */}
          <div className="cart-summary">
            <div className="summary-row">
              <span>Items ({itemCount}):</span>
              <span>${totalPrice.toFixed(2)}</span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span className="total-amount">${totalPrice.toFixed(2)}</span>
            </div>
            
            <div className="cart-actions">
              <button 
                className="continue-shopping-btn"
                onClick={() => navigate('/products')}
              >
                Continue Shopping
              </button>
              <button 
                className="checkout-btn"
                onClick={handleCheckout}
                disabled={loading}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default CartPage;
